import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { deleteOrderDetail, fetchOrderDetailsList } from "../../shared/commerceMastersApi";
import { normalizeOrderItemStatus } from "../../shared/orderItemStatuses";
import { resolveMediaUrl } from "../../shared/mediaUrl";
import { mastersDataGridSx } from "../../shared/mastersDataGridSx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

function ProductThumb({ image, name }) {
  const src = resolveMediaUrl(image);
  if (!src) return <span>—</span>;
  return (
    <img
      src={src}
      alt={name || "Product"}
      className="order-product-thumb"
      style={{ width: 42, height: 42 }}
    />
  );
}

export function OrderDetailsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const total = items.length;

  const load = async () => {
    setLoading(true);
    setError(null);
    const [iRes, oRes, pRes, vRes] = await Promise.all([
      fetchOrderDetailsList(),
      apiGet("/api/Orders"),
      apiGet("/api/Products"),
      apiGet("/api/Vendors")
    ]);
    if (!iRes.ok) {
      setError(iRes.error ?? "Failed to load order details");
      setItems([]);
    } else {
      setItems(iRes.items);
    }
    if (oRes.ok) setOrders(oRes.data ?? []);
    if (pRes.ok) setProducts(pRes.data ?? []);
    if (vRes.ok) setVendors(vRes.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const onEdit = (x) => {
    navigate(`/orders?edit=${x.orderId}&tab=details&detailEdit=${x.id}`);
  };

  const onDelete = async (id) => {
    if (!(await window.confirmDialog("Delete this order detail?"))) return;
    const res = await deleteOrderDetail(id);
    if (!res.ok) setError(res.error ?? "Failed to delete order detail");
    else await load();
  };

  const orderById = useMemo(() => {
    const map = new Map();
    for (const order of orders) map.set(order.id, order);
    return map;
  }, [orders]);

  const productById = useMemo(() => {
    const map = new Map();
    for (const product of products) map.set(product.id, product);
    return map;
  }, [products]);

  const vendorById = useMemo(() => {
    const map = new Map();
    for (const vendor of vendors) map.set(vendor.id, vendor);
    return map;
  }, [vendors]);

  const rows = useMemo(
    () =>
      items.map((item) => {
        const product = productById.get(item.productId);
        return {
          id: item.id,
          detail: item,
          orderNo: orderById.get(item.orderId)?.orderNo || `#${item.orderId}`,
          productName: product?.name || `#${item.productId}`,
          productImage: product?.image || "",
          vendorName: vendorById.get(item.vendorId)?.name || `#${item.vendorId}`,
          grandTotal: item.grandTotal ?? 0,
          itemStatus: normalizeOrderItemStatus(item.itemStatus ?? item.ItemStatus)
        };
      }),
    [items, orderById, productById, vendorById]
  );

  const columns = [
      {
        field: "productImage",
        headerName: "Photo",
        width: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => <ProductThumb image={params.row.productImage} name={params.row.productName} />
      },
      { field: "orderNo", headerName: "Order", flex: 1, minWidth: 130 },
      { field: "productName", headerName: "Product", flex: 1, minWidth: 140 },
      { field: "vendorName", headerName: "Vendor", flex: 1, minWidth: 140 },
      { field: "itemStatus", headerName: "Item status", minWidth: 140 },
      { field: "grandTotal", headerName: "Grand Total", width: 120 },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <div className="table-actions">
            <button className="icon-button" type="button" title="Edit" onClick={() => onEdit(params.row.detail)}>
              ✏️
            </button>
            <button className="icon-button danger" type="button" title="Delete" onClick={() => void onDelete(params.row.detail.id)}>
              🗑️
            </button>
          </div>
        )
      }
  ];

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Order Details</h2>
          <p>{loading ? "Loading..." : `Total: ${total}`}</p>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={mastersDataGridSx}
          />
        </div>
      </div>
    </div>
  );
}
